import * as React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Link,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Icon,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  Tabs,
  Tab,
} from '@material-ui/core';

import DeleteIcon from '@material-ui/icons/Delete';
import { LoadingCircle, Dropzone, TitleBackButton, isMultipleCampusAdmin } from '../../common';
import { OtherAssessmentTypes } from '../+store/assessments/assessments.types';
import UploadModal from '../UploadModal';
import DeleteModal from '../DeleteModal';
import AssessmentGroupManager from '../AssessmentGroupManager';
import { storage } from '../../App';
import moment from 'moment';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const isDemo = process.env.REACT_APP_THEME === 'demo';

// How long the status banner (e.g. the "wrong CCMR file" warning) stays up before it
// auto-dismisses. Long enough to read the instructional message, then it clears itself.
const STATUS_BANNER_AUTO_DISMISS_MS = 15000;

// Multi-file upload parse gate. Between batch files we wait for the server-side
// parse chain to finish (concurrent chunked parses can starve each other), driven
// by the live assessmentSets listener plus this timer for the timeout paths.
const PARSE_WAIT_TICK_MS = 5000;
// A chunk execution can legitimately run up to the function's 540s cap, so only
// call the chain dead after well more than one full execution with no write.
const PARSE_STALL_MS = 8 * 60 * 1000;
// Types with no parser never write a parsed cycle at all; give the listener a
// moment to catch up, then advance rather than hang the queue.
const PARSE_NO_CYCLE_GRACE_MS = 20 * 1000;

// Get school year string from a date (Texas school year: Aug-July)
// Returns format like "2025-2026" for dates from Aug 2025 through July 2026
const getSchoolYear = (date) => {
  if (!date) return null;
  const m = moment(date);
  if (!m.isValid()) return null;
  const month = m.month(); // 0-indexed (0 = Jan, 7 = Aug)
  const year = m.year();
  // Aug-Dec = first half of school year, Jan-July = second half
  const startYear = month >= 7 ? year : year - 1;
  return `${startYear}-${startYear + 1}`;
};

// Extract year from assessment type (e.g., "EOC2026" -> 2026, "STAAR2025" -> 2025)
const getYearFromType = (type) => {
  if (!type) return null;
  const match = type.match(/(\d{4})$/);
  return match ? parseInt(match[1], 10) : null;
};

// Some assessment types carry a trailing number that is NOT an administration year:
// SAT is permanently typed `SAT2019` (a frozen format-version tag) and `ACT` has no
// year at all. For these, deriving the school year from the type would file every
// upload under 2018-2019 (SAT) or nothing (ACT), so they'd drop out of the current-
// year view. Bucket those by the file's upload date instead.
const TYPE_YEAR_IS_FORMAT_VERSION = (type) => type === 'SAT2019' || /^ACT(\d*)$/.test(type || '');

// Get school year for an assessment, trying date first, then type, then upload date.
const getAssessmentSchoolYear = (assessment) => {
  // Try an explicit date first
  const fromDate = getSchoolYear(assessment.date);
  if (fromDate) return fromDate;

  // Then the year embedded in the type (e.g., EOC2026 -> 2025-2026), unless that
  // trailing number is a format version rather than a test year.
  if (!TYPE_YEAR_IS_FORMAT_VERSION(assessment.type)) {
    const yearFromType = getYearFromType(assessment.type);
    if (yearFromType) {
      // Assessment year (e.g., 2026) refers to the spring of that school year
      // So EOC2026 is for school year 2025-2026
      return `${yearFromType - 1}-${yearFromType}`;
    }
  }

  // Finally, fall back to when the file was actually uploaded (covers SAT/ACT).
  const uploadedAt = assessment.file && assessment.file.uploadedAt;
  const fromUpload = getSchoolYear(uploadedAt);
  if (fromUpload) return fromUpload;

  return null;
};

// Get current school year based on today's date
const getCurrentSchoolYear = () => getSchoolYear(moment());

// Format assessment type for display
// A finished parse that matched nobody. Only parsers that count students write a numeric
// studentsUpdated, so legacy boolean `parsed` and listing-style files (no count) stay green.
const importedNoStudents = (assessment) =>
  !!assessment &&
  !!assessment.parsed &&
  assessment.parsed.finished === true &&
  assessment.parsed.studentsUpdated === 0 &&
  !assessment.parsed.studentsIgnored;

const noStudentsMessage = (assessment) => {
  const duplicateOf = assessment.parsed && assessment.parsed.duplicateOf;
  if (duplicateOf && duplicateOf.name) {
    return (
      `This file is identical to the one already uploaded as "${duplicateOf.name}", so it was not imported. ` +
      `Export the correct test and upload it again, then delete this entry.`
    );
  }
  const skipped = (assessment.parsed && assessment.parsed.skippedBlocks) || [];
  if (skipped.length) {
    return (
      `No students were imported. The subject could not be determined from the test title: ` +
      `${skipped.map((title) => `"${title}"`).join(', ')}. ` +
      `Rename the test so the title includes the subject (Reading, Math, Science, Algebra, ...) and re-export.`
    );
  }
  return (
    'No students were imported. Check that the file has a Local ID column and that the IDs match the roster, ' +
    'or re-export the file and upload it again.'
  );
};

const getDisplayType = (type) => {
  if (type === 'ComboAssessment') return 'MAP Growth';
  return type;
};
const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '26px',
    marginRight: '26px',
  },
  yearFilterSidebar: {
    width: '150px',
    minWidth: '150px',
    flexShrink: 0,
    paddingRight: theme.spacing.unit * 2,
    borderRight: '1px solid #e0e0e0',
    marginRight: theme.spacing.unit * 3,
  },
  yearFilterTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing.unit,
    fontSize: '14px',
  },
  yearCheckboxLabel: {
    marginLeft: 0,
    marginRight: 0,
  },
  mainContent: {
    flex: 1,
    minWidth: 0,
  },
  background: {},
  content: {
    height: '500px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progress: {},
  dropzone: {
    border: isDemo ? '1px solid #C4C4C4' : '1px solid #656565',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    flexDirection: 'column',
  },
  dropzoneWrapper: {
    height: '300px',
    marginBottom: '36px',
  },
  uploadResults: {
    textAlign: 'left',
    maxHeight: '240px',
    overflowY: 'auto',
    marginBottom: '24px',
  },
  dropzoneText: {
    fontSize: '24px',
    color: '#a9a9a9',
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  tableWrapper: {
    marginBottom: '24px',
    background: 'unset',
  },
  // Pin the header row while scrolling the (bounded-height) table body. MUI v3 has
  // no stickyHeader prop, so apply position: sticky to the header cells directly.
  // A solid background keeps body rows from showing through as they scroll under it.
  stickyHeader: {
    '& th': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      backgroundColor: '#fff',
    },
  },
  headerGrid: {
    alignItems: 'center',
  },
  button: {
    marginLeft: theme.spacing.unit,
  },
  rightIcon: {
    marginLeft: theme.spacing.unit,
  },
  leftIcon: {
    marginRight: theme.spacing.unit,
  },
  deleteModal: {
    position: 'absolute',
    width: theme.spacing.unit * 50,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing.unit * 4,
    outline: 'none',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
  },
  headerDiv: {
    marginLeft: '26px',
    marginTop: '3px',
  },
  tableCell: {
    padding: isDemo ? '3px 8px' : '4px 16px',
    textTransform: 'capitalize',
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.unit,
  },
  formControl: {
    minWidth: 120,
    maxWidth: 300,
  },
});

class AssessmentManager extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    loading: PropTypes.bool.isRequired,
    profile: PropTypes.object,
    needsInit: PropTypes.bool.isRequired,
    hasLoaded: PropTypes.bool,
    addAssessmentsListener: PropTypes.func.isRequired,
    removeAssessmentsListener: PropTypes.func.isRequired,
    clearStatus: PropTypes.func,
    status: PropTypes.shape({
      error: PropTypes.string,
      message: PropTypes.string,
    }).isRequired,
    assessmentList: PropTypes.array.isRequired,
    onDrop: PropTypes.func.isRequired,
    uploadStaging: PropTypes.object,
    onCancelUpload: PropTypes.func.isRequired,
    uploadProgressMsg: PropTypes.string.isRequired,
    doUpload: PropTypes.func.isRequired,
    uploadOpen: PropTypes.bool.isRequired,
    onUploadBtnClick: PropTypes.func.isRequired,
    onCancelUploadClick: PropTypes.func.isRequired,
    handleDelete: PropTypes.func.isRequired,
    schoolList: PropTypes.object,
    uploaderList: PropTypes.array,
    onAssessmentVisibilityBySuperAdminOnlyChange: PropTypes.func.isRequired,
    onDownloadClick: PropTypes.func.isRequired,
    onReparseClick: PropTypes.func.isRequired,
  };
  state = {
    // 0 = the uploaded files, 1 = custom assessment groups. Groups used to sit below the
    // full file list, so finding them meant scrolling past every upload.
    activeTab: 0,
    errorMsgs: [],
    isDeleteModalOpen: false,
    assessmentToBeDeleted: null,
    selectedUploader: '',
    selectedYears: new Set([getCurrentSchoolYear()]), // Default to current school year
    reparsingAssessments: new Set(), // Track assessments being reparsed
    interimTemplateClicked: false,
    expandedEocRows: new Set(),
    // Multi-file upload queue. uploadStaging / uploadProgressMsg are single-slot in
    // the store, so files have to run one at a time: starting the next before the
    // current one settles would clobber both.
    uploadQueue: [], // files still waiting their turn
    uploadQueueTotal: 0, // size of the batch, for "file N of M"
    currentUploadFile: null, // the file being validated/uploaded right now
    uploadResults: [], // { name, ok, message } per finished file
    batchStartedAtIso: null, // lower bound for matching sets this batch created
    parseWait: null, // waiting on the current file's server-side parse (mid-batch only)
    pendingResult: null, // the current file's result, held until its parse settles
  };

  componentDidMount() {
    this.shouldInit();
  }

  componentDidUpdate(prevProps, prevState) {
    this.shouldInit();
    const { assessmentList, status } = this.props;
    if (prevState.isDeleteModalOpen && assessmentList.length < prevProps.assessmentList.length) {
      this.setState({
        isDeleteModalOpen: false,
        assessmentToBeDeleted: null,
      });
    }
    // Auto-dismiss the status banner once a new one appears (e.g. a wrong-file warning),
    // so it doesn't sit on screen indefinitely or survive navigating away and back.
    const prevError = prevProps.status ? prevProps.status.error : '';
    const prevMessage = prevProps.status ? prevProps.status.message : '';
    const isNewBanner =
      (status.error && status.error !== prevError) || (status.message && status.message !== prevMessage);
    if (isNewBanner) {
      clearTimeout(this.statusBannerTimer);
      this.statusBannerTimer = setTimeout(() => {
        if (this.props.clearStatus) this.props.clearStatus();
      }, STATUS_BANNER_AUTO_DISMISS_MS);
    }
    this.advanceUploadQueue(prevProps);
    // Parse-status writes stream in through the assessments listener as prop
    // updates; react to them immediately rather than waiting for the next tick.
    this.checkParseWait();
  }

  // The store emits no per-file "done" event, so the current file is considered
  // settled by the transition it leaves behind:
  //   - the upload spinner clearing (finished, successfully or not)
  //   - a validation error arriving before any upload started (wrong file type,
  //     plan limit, unrecognized content)
  //   - uploadStaging clearing with nothing else changing (user cancelled the modal)
  // Whichever fires, record the outcome and start the next file.
  advanceUploadQueue = (prevProps) => {
    const { currentUploadFile, parseWait } = this.state;
    if (!currentUploadFile || parseWait) return;
    const { uploadStaging, uploadProgressMsg, status } = this.props;
    const wasUploading = !!prevProps.uploadProgressMsg;
    const isUploading = !!uploadProgressMsg;
    const statusChanged = prevProps.status !== status;
    const name = currentUploadFile.name;

    let result = null;
    if (wasUploading && !isUploading) {
      result = status.error ? { name, ok: false, message: status.error } : { name, ok: true, message: 'Uploaded' };
    } else if (!isUploading && !uploadStaging && statusChanged && status.error) {
      result = { name, ok: false, message: status.error };
    } else if (prevProps.uploadStaging && !uploadStaging && !isUploading && !wasUploading && !statusChanged) {
      result = { name, ok: false, message: 'Skipped' };
    }
    if (!result) return;

    // Server-side parsing continues after the upload callable returns, and two
    // chunked parses running at once can starve each other (seen on staging: a
    // TELPAS file left at finished:false / 0 rows in a 3-file batch; the same
    // file alone parsed all 2857). So when more files are waiting, hold the
    // queue until this file's set reports parsed.finished. The LAST file has
    // nothing behind it, so it advances immediately — which also keeps the
    // single-file upload exactly as it always was.
    if (result.ok && this.state.uploadQueue.length > 0) {
      this.setState({
        parseWait: {
          name,
          // Only match a set this batch created/updated, not an older one that
          // happens to share the filename. uploadedAt is written client-side, so
          // the clocks are the same.
          uploadedAfterIso: this.state.batchStartedAtIso,
          lastParsedJson: null,
          lastProgressAt: Date.now(),
        },
        pendingResult: result,
      });
      clearInterval(this.parseWaitTimer);
      this.parseWaitTimer = setInterval(this.checkParseWait, PARSE_WAIT_TICK_MS);
      this.checkParseWait();
      return;
    }

    this.advanceToNext(result);
  };

  // Resolve the set the current file landed in and decide whether its parse is
  // done. parsed is trustworthy at this point because processAll resets it to
  // {finished:false} BEFORE the upload callable returns: chunked types show
  // finished:false until the chain completes, synchronous parsers already show
  // finished:true, and types with no parser still show literal false — those
  // must not wait, or an "Other" file would hang the queue.
  checkParseWait = () => {
    const { parseWait } = this.state;
    if (!parseWait) return;
    const { assessmentList } = this.props;
    const candidates = (assessmentList || []).filter(
      (a) =>
        a.file && a.file.origName === parseWait.name && String(a.file.uploadedAt) >= String(parseWait.uploadedAfterIso)
    );
    const set = candidates.sort((a, b) => String(b.file.uploadedAt).localeCompare(String(a.file.uploadedAt)))[0];
    const parsed = set && set.parsed;

    if (parsed === true || (parsed && parsed.finished)) {
      const count = parsed && parsed.studentsUpdated;
      if (importedNoStudents(set)) {
        this.finishParseWait('Uploaded, but 0 students were imported. See the warning on the file.');
        return;
      }
      this.finishParseWait(count != null ? `Uploaded (${count} students)` : 'Uploaded');
      return;
    }

    if (parsed && typeof parsed === 'object') {
      // In flight. Any change in the parsed node counts as progress; only a
      // chain that has genuinely stopped writing for PARSE_STALL_MS gets
      // abandoned (the file is uploaded either way — only the wait ends).
      const parsedJson = JSON.stringify(parsed);
      if (parsedJson !== parseWait.lastParsedJson) {
        this.setState({
          parseWait: { ...parseWait, lastParsedJson: parsedJson, lastProgressAt: Date.now() },
        });
        return;
      }
      if (Date.now() - parseWait.lastProgressAt > PARSE_STALL_MS) {
        this.finishParseWait('Uploaded; still processing in the background');
      }
      return;
    }

    // No parse cycle started (unparsed type, or listener catching up). Give the
    // listener a short grace window, then move on.
    if (Date.now() - parseWait.lastProgressAt > PARSE_NO_CYCLE_GRACE_MS) {
      this.finishParseWait('Uploaded');
    }
  };

  finishParseWait = (message) => {
    clearInterval(this.parseWaitTimer);
    const { pendingResult } = this.state;
    const result = { ...pendingResult, message };
    this.setState({ parseWait: null, pendingResult: null }, () => this.advanceToNext(result));
  };

  advanceToNext = (result) => {
    this.setState(
      (prev) => {
        const [next, ...rest] = prev.uploadQueue;
        return {
          uploadResults: [...prev.uploadResults, result],
          uploadQueue: rest,
          currentUploadFile: next || null,
          defaultAssessmentName: next ? next.name.split('.')[0] : prev.defaultAssessmentName,
        };
      },
      () => {
        const { currentUploadFile: nextFile, uploadQueueTotal } = this.state;
        if (nextFile) {
          const { onDrop, profile } = this.props;
          onDrop([nextFile], profile);
          return;
        }
        // Queue drained. A single-file upload returns straight to the list the way
        // it always has; a batch stays put so its results list can be read.
        if (uploadQueueTotal <= 1) {
          this.props.onCancelUploadClick();
        }
      }
    );
  };

  componentWillUnmount() {
    const { profile, removeAssessmentsListener, clearStatus } = this.props;
    clearTimeout(this.statusBannerTimer);
    clearInterval(this.parseWaitTimer);
    if (profile) {
      removeAssessmentsListener(profile);
    }
    // Clear any lingering status banner so it isn't still showing on return.
    if (clearStatus) clearStatus();
  }

  handleChangeFilter = (event) => {
    this.setState({ selectedUploader: event.target.value });
  };

  // Get sorted list of unique school years from assessments
  getAvailableYears = () => {
    const { assessmentList } = this.props;
    const years = new Set();
    assessmentList.forEach((assessment) => {
      const year = getAssessmentSchoolYear(assessment);
      if (year) years.add(year);
    });
    return Array.from(years)
      .sort()
      .reverse(); // Most recent first
  };

  handleYearFilterChange = (year) => (event) => {
    const { selectedYears } = this.state;
    const newSelectedYears = new Set(selectedYears);
    if (event.target.checked) {
      newSelectedYears.add(year);
    } else {
      newSelectedYears.delete(year);
    }
    this.setState({ selectedYears: newSelectedYears });
  };

  isInSelectedYear = (assessment) => {
    const { selectedYears } = this.state;
    if (selectedYears.size === 0) return true; // Show all if none selected
    const year = getAssessmentSchoolYear(assessment);
    return year && selectedYears.has(year);
  };

  // Get uploaders that have assessments in the selected years
  getFilteredUploaderList = () => {
    const { assessmentList, uploaderList } = this.props;
    const { selectedYears } = this.state;

    if (selectedYears.size === 0) return uploaderList;

    // Get owners from assessments in selected years
    const ownersInSelectedYears = new Set();
    assessmentList.forEach((assessment) => {
      const year = getSchoolYear(assessment.date);
      if (year && selectedYears.has(year) && assessment.owner) {
        ownersInSelectedYears.add(assessment.owner);
      }
    });

    return uploaderList.filter((item) => ownersInSelectedYears.has(item.value));
  };

  renderFilterSidebar = () => {
    const { classes } = this.props;
    const { selectedYears, selectedUploader } = this.state;
    const availableYears = this.getAvailableYears();
    const filteredUploaderList = this.getFilteredUploaderList();

    // Don't show sidebar if no years to filter
    if (availableYears.length <= 1) return null;

    return (
      <div className={classes.yearFilterSidebar}>
        <Typography className={classes.yearFilterTitle}>School Year</Typography>
        <FormGroup>
          {availableYears.map((year) => (
            <FormControlLabel
              key={year}
              className={classes.yearCheckboxLabel}
              control={
                <Checkbox
                  checked={selectedYears.has(year)}
                  onChange={this.handleYearFilterChange(year)}
                  color="primary"
                  size="small"
                />
              }
              label={year}
            />
          ))}
        </FormGroup>

        <Typography className={classes.yearFilterTitle} style={{ marginTop: '16px' }}>
          Uploaded By
        </Typography>
        <FormControl className={classes.formControl} fullWidth>
          <Select
            value={selectedUploader}
            onChange={this.handleChangeFilter}
            displayEmpty
            inputProps={{ name: 'uploaded-by', id: 'uploaded-by' }}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {filteredUploaderList.map((name) => (
              <MenuItem key={name.value} value={name.value}>
                {_.startCase(name.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  };

  // EOC has 3 admin windows (Spring, December, Summer) that all fold into one assessmentSet entry.
  // Show a chevron when otherFiles exist so the user can see which files are contributing.
  isExpandableEoc = (assessment) =>
    /^EOC\d{4}$/.test(assessment.type) && assessment.otherFiles && Object.keys(assessment.otherFiles).length > 0;

  toggleEocExpand = (assessmentId) => {
    const { expandedEocRows } = this.state;
    const next = new Set(expandedEocRows);
    if (next.has(assessmentId)) next.delete(assessmentId);
    else next.add(assessmentId);
    this.setState({ expandedEocRows: next });
  };

  // Map TEA administration date code to a human-readable chip label.
  // 13xx = December, 15xx = Spring, 16xx = Summer.
  // The last two digits are the 2-digit school year (e.g. 1526 → Spring 2026).
  getAdminWindowLabel = (code) => {
    if (!code || code.length < 4) return null;
    const prefix = code.slice(0, 2);
    const year = `20${code.slice(2, 4)}`;
    if (prefix === '13') return `December ${year}`;
    if (prefix === '15') return `Spring ${year}`;
    if (prefix === '16') return `Summer ${year}`;
    return null;
  };

  // Return all contributing files sorted most-recent-first.
  // Files with a stored administrationCode are already deduplicated at upload time
  // (createAssessmentSet replaces same-window entries). Older entries without a
  // code are shown as-is for backward compatibility.
  getAllEocFiles = (assessment) => {
    const files = [];
    if (assessment.file) files.push(assessment.file);
    Object.values(assessment.otherFiles || {}).forEach((f) => files.push(f));
    return files.sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));
  };

  deleteIcon = (assessment) => {
    const canDelete = this.canDelete(assessment.owner);
    return (
      <IconButton onClick={this.deleteButtonClicked(assessment)} disabled={!canDelete}>
        <DeleteIcon color={canDelete ? 'error' : 'disabled'} />
      </IconButton>
    );
  };

  deleteButtonClicked = (assessment) => () => {
    this.setState({
      isDeleteModalOpen: true,
      assessmentToBeDeleted: assessment,
    });
  };

  renderOwner = (owner) => {
    const { schoolList } = this.props;
    const didSchoolListLoad = schoolList && Object.keys(schoolList).length;
    if (owner !== 'district' && didSchoolListLoad) {
      if (typeof owner === 'string') {
        // uploaded by single-campus admin
        return schoolList[owner] ? schoolList[owner].name || owner : owner;
      } else {
        if (owner) {
          // uploaded by multiple-campus admin
          return owner.map((campus) => (schoolList[campus] ? schoolList[campus].name || campus : campus)).join(', ');
        } else {
          return 'No Owner';
        }
      }
    }
    return owner;
  };

  isOwner = (assessment) => {
    const { owner } = assessment;
    const { selectedUploader } = this.state;
    if (!selectedUploader) {
      return true;
    }
    if (owner == null || typeof owner === 'string') {
      return owner === selectedUploader;
    }
    if (owner) {
      return owner.includes(selectedUploader);
    }
  };

  canDelete = (owner) => {
    /*
     *  1. District Admins can delete any file uploaded by district or campus admins
     *  2. Single Campus Admins can only delete files that belong to their campus only
     *  3. Multiple Campus Admin can delete files that belong to any of their campuses.
     * */
    const { profile } = this.props;
    if (profile && profile.role === 'districtAdmin') {
      return true;
    }
    if (owner === 'district') {
      return false;
    }
    if (isMultipleCampusAdmin(profile)) {
      const campuses = profile.campusNumber;
      return typeof owner === 'string' ? campuses.includes(owner) : _.isEqual(_.sortBy(owner), _.sortBy(campuses));
    }
    return typeof owner === 'string' && profile.campusNumber === owner;
  };

  renderActionButtons = () => {
    const { classes, onUploadBtnClick } = this.props;
    return (
      <div className={classes.actionContainer} style={{ justifyContent: 'flex-start' }}>
        <Button variant="contained" color="primary" className={classes.button} onClick={onUploadBtnClick}>
          Upload Assessment Files
          <Icon className={classes.rightIcon}>cloud_upload</Icon>
        </Button>

        <Button variant="contained" color="primary" className={classes.button} onClick={this.onInterimTemplateClick}>
          Download Interim Template
          <Icon className={classes.rightIcon}>download</Icon>
        </Button>
      </div>
    );
  };

  handleDelete = (assessment) => () => {
    const { handleDelete, profile } = this.props;
    handleDelete(profile, assessment);
  };

  onSelectFromComputerClick = () => {
    this.dropZoneRef.open();
  };

  onDrop = (accepted, rejected) => {
    const rejectedResults = (rejected || []).map((f) => ({
      name: f.name,
      ok: false,
      message: 'This file type cannot be processed',
    }));
    if (!accepted || !accepted.length) {
      this.setState({ uploadResults: rejectedResults, errorMsgs: [] });
      return;
    }
    const [first, ...rest] = accepted;
    this.setState(
      {
        errorMsgs: [],
        uploadResults: rejectedResults,
        uploadQueue: rest,
        uploadQueueTotal: accepted.length,
        currentUploadFile: first,
        // Sets record uploadedAt from this same clock, so this cleanly separates
        // sets this batch touches from older ones with the same filename.
        batchStartedAtIso: new Date().toISOString(),
        parseWait: null,
        pendingResult: null,
        defaultAssessmentName: first.name.split('.')[0],
      },
      () => {
        const { onDrop, profile } = this.props;
        onDrop([first], profile);
      }
    );
  };

  clearUploadResults = () => {
    this.setState({ uploadResults: [], uploadQueueTotal: 0 });
  };

  // Leaving the upload view abandons anything still queued. The file already in
  // flight can't be recalled, but nothing new gets started.
  handleBackFromUpload = () => {
    const { onCancelUploadClick } = this.props;
    clearInterval(this.parseWaitTimer);
    this.setState({
      uploadQueue: [],
      uploadQueueTotal: 0,
      currentUploadFile: null,
      uploadResults: [],
      parseWait: null,
      pendingResult: null,
    });
    onCancelUploadClick();
  };

  saveDropZoneRef = (dropZoneRef) => {
    this.dropZoneRef = dropZoneRef;
  };

  doUpload = (uploadStaging, fields) => {
    const { profile, doUpload } = this.props;
    doUpload(uploadStaging, fields, profile);
  };

  handleDownload = async (assessment) => {
    const { profile, onDownloadClick } = this.props;
    try {
      onDownloadClick(assessment);
    } catch (error) {
      console.error('Error downloading assessment set', error);
    }
  };

  handleReparse = async (assessment) => {
    const { onReparseClick } = this.props;
    const { reparsingAssessments } = this.state;
    try {
      // Immediately show hourglass by marking as reparsing
      const newReparsing = new Set(reparsingAssessments);
      newReparsing.add(assessment.id);
      this.setState({ reparsingAssessments: newReparsing });
      onReparseClick(assessment);
    } catch (error) {
      console.error('Error Reparse assessment set', error);
    }
  };

  handleAssessmentVisibilityBySuperAdminOnlyChange = async (assessment, isChecked) => {
    const { profile, onAssessmentVisibilityBySuperAdminOnlyChange } = this.props;
    try {
      assessment.isVisibleBySuperAdminOnly = isChecked;
      onAssessmentVisibilityBySuperAdminOnlyChange(profile, assessment);
    } catch (error) {
      console.error('Error on change assessment visibility', error);
    }
  };

  doUnrecognizedFileUpload = (uploadStaging, fields) => {
    const { profile, doUpload } = this.props;
    doUpload(uploadStaging, fields, profile, true);
  };

  cancelDelete = () => {
    this.setState({
      isDeleteModalOpen: false,
    });
  };

  onInterimTemplateClick = async () => {
    const ref = storage.ref('public/InterimAssessmentTemplate.csv');
    const url = await ref.getDownloadURL();
    window.open(url);
    this.setState({ interimTemplateClicked: true });
  };
  renderUploadModal = () => {
    const { classes } = this.props;
    const { uploadQueue, uploadQueueTotal, currentUploadFile, uploadResults, parseWait } = this.state;
    const isBatch = uploadQueueTotal > 1;
    const doneCount = uploadQueueTotal - uploadQueue.length - (currentUploadFile ? 1 : 0);
    // While the parse gate holds the queue, show processing progress if the
    // parser has reported a row count yet.
    let progressVerb = 'Uploading';
    let progressSuffix = '';
    if (parseWait) {
      progressVerb = 'Processing';
      if (parseWait.lastParsedJson) {
        const parsed = JSON.parse(parseWait.lastParsedJson);
        if (parsed && parsed.studentsUpdated) {
          progressSuffix = ` (${parsed.studentsUpdated} students so far)`;
        }
      }
    }
    return (
      <React.Fragment>
        <Button onClick={this.handleBackFromUpload}>
          <Icon className={classes.leftIcon}>chevron_left</Icon>
          Back
        </Button>
        <div className={classes.dropzoneWrapper}>
          <Dropzone
            disableClick
            accept={['text/plain', 'application/vnd.ms-excel', 'application/pdf', 'text/csv', '.dat', '.xls', '.xlsx']}
            ref={this.saveDropZoneRef}
            onDrop={this.onDrop}
            multiple
          >
            <div className={classes.dropzone}>
              <div className={classes.dropzoneText}>Drag your files here or</div>
              <Button variant="contained" color="primary" onClick={this.onSelectFromComputerClick}>
                Select from Computer
              </Button>
            </div>
          </Dropzone>
        </div>
        {isBatch && currentUploadFile && (
          <Typography align="center" variant="subtitle1" className={classes.messages}>
            {`${progressVerb} file ${doneCount + 1} of ${uploadQueueTotal}: ${currentUploadFile.name}${progressSuffix}`}
          </Typography>
        )}
        {uploadResults.length > 0 && (
          <div className={classes.uploadResults}>
            <Typography variant="subtitle1">
              {currentUploadFile ? 'Finished so far' : `Finished ${uploadResults.length} file(s)`}
            </Typography>
            {uploadResults.map((r, index) => (
              <Typography key={`upload-result-${index}`} variant="body2" color={r.ok ? 'inherit' : 'error'}>
                {`${r.ok ? '✓' : '✗'} ${r.name}: ${r.message}`}
              </Typography>
            ))}
            {!currentUploadFile && (
              <Button color="primary" onClick={this.clearUploadResults}>
                Clear
              </Button>
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  handleTabChange = (event, activeTab) => {
    this.setState({ activeTab });
  };

  renderAssessmentsList = () => {
    const { classes, assessmentList, profile, needsInit, loading, hasLoaded } = this.props;
    const { errorMsgs } = this.state;
    // Treat the page as loading until the first snapshot actually arrives.
    // needsInit goes false as soon as the listener registers (before any data),
    // so without hasLoaded we'd briefly flash "No Assessment Uploaded Yet!".
    const isLoading = !hasLoaded || needsInit || loading;
    return (
      <React.Fragment>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
          {this.renderActionButtons()}
        </div>
        {errorMsgs.length > 0 && (
          <div>
            {errorMsgs.map((msg, errIndex) => (
              <div key={`error-${errIndex}`}>
                <Typography color="error">{msg}</Typography>
              </div>
            ))}
          </div>
        )}
        {assessmentList.length === 0 ? (
          <Typography className={classes.tableWrapper}>
            {isLoading ? 'Loading Assessments ...' : 'No Assessment Uploaded Yet!'}
          </Typography>
        ) : (
          // No inner max-height/overflow here: a scroll box latches the wheel, so reaching
          // the bottom of the list stopped the page scrolling until the pointer moved into
          // the margin. The list is its own tab now, so it can simply be as tall as it is.
          <Paper className={classes.tableWrapper} style={{ width: '100%' }}>
            <Table className={classes.table}>
              <TableHead className={classes.stickyHeader}>
                <TableRow>
                  {profile && profile.type == 'superAdmin' && (
                    <TableCell className={classes.tableCell} align="center">
                      Reparse
                    </TableCell>
                  )}
                  <TableCell className={classes.tableCell} align="center">
                    Delete
                  </TableCell>
                  <TableCell className={classes.tableCell}>Name</TableCell>
                  <TableCell className={classes.tableCell}>Type</TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Uploaded By
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Processed
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Updated Students
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Unmatched Students
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Uploaded At
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Date
                  </TableCell>
                  <TableCell className={classes.tableCell} align="center">
                    Cut Points
                  </TableCell>
                  {profile && (profile.type == 'superAdmin' || profile.isSuperAdmin == true) && (
                    <TableCell className={classes.tableCell} align="center">
                      Super Admin Only
                    </TableCell>
                  )}
                  {profile && profile.type == 'superAdmin' && (
                    <TableCell className={classes.tableCell} align="center">
                      Download
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {assessmentList
                  .filter((a) => !a.hiddenFromUser)
                  .filter(this.isOwner)
                  .filter(this.isInSelectedYear)
                  .map((assessment) => {
                    const colCount =
                      11 +
                      (profile && profile.type === 'superAdmin' ? 2 : 0) +
                      (profile && profile.isSuperAdmin ? 1 : 0);
                    const eocExpanded = this.state.expandedEocRows.has(assessment.id);
                    return (
                      <React.Fragment key={assessment.id}>
                        <TableRow>
                          {profile && profile.type == 'superAdmin' && (
                            <TableCell className={classes.tableCell} align="center">
                              <Button
                                size="small"
                                color="primary"
                                className={classes.IconButton}
                                onClick={(e) => this.handleReparse(assessment)}
                              >
                                <Icon className={classes.center}>sync</Icon>
                              </Button>
                            </TableCell>
                          )}
                          <TableCell className={classes.tableCell} align="center">
                            {this.deleteIcon(assessment)}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              {this.isExpandableEoc(assessment) && (
                                <IconButton
                                  size="small"
                                  onClick={() => this.toggleEocExpand(assessment.id)}
                                  style={{ padding: 2, marginRight: 4 }}
                                  title="Show contributing files"
                                >
                                  <Icon style={{ fontSize: 18 }}>
                                    {this.state.expandedEocRows.has(assessment.id) ? 'expand_more' : 'chevron_right'}
                                  </Icon>
                                </IconButton>
                              )}
                              <Typography>{assessment.name}</Typography>
                            </div>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Typography>{getDisplayType(assessment.type)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            <Typography>{this.renderOwner(assessment.owner)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            {assessment.parsed &&
                            assessment.parsed.finished &&
                            !this.state.reparsingAssessments.has(assessment.id) ? (
                              importedNoStudents(assessment) ? (
                                <Icon
                                  style={{ color: '#f5a623' }}
                                  className={classes.noIcon}
                                  title={noStudentsMessage(assessment)}
                                >
                                  warning
                                </Icon>
                              ) : (
                                <Icon style={{ color: '#79f5ab' }} className={classes.yesIcon}>
                                  check_circle
                                </Icon>
                              )
                            ) : assessment.type === OtherAssessmentTypes.Other &&
                              !this.state.reparsingAssessments.has(assessment.id) ? (
                              <Icon
                                style={{ color: '#f58787' }}
                                className={classes.noIcon}
                                title="Unsupported file type, not processed. Re-upload as CSV to import the data."
                              >
                                cancel
                              </Icon>
                            ) : (
                              <Icon style={{ color: '#FFFF00' }} className={classes.noIcon}>
                                hourglass_full
                              </Icon>
                            )}
                          </TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            {importedNoStudents(assessment) ? (
                              <Typography
                                style={{ color: '#f5a623', fontWeight: 600 }}
                                title={noStudentsMessage(assessment)}
                              >
                                0
                              </Typography>
                            ) : (
                              <Typography>
                                {assessment.parsed ? assessment.parsed.studentsUpdated || '-' : '-'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            {assessment.parsed &&
                            assessment.parsed.ignoredStudentsList &&
                            assessment.parsed.ignoredStudentsList.length > 0 ? (
                              <Link
                                component="button"
                                variant="body2"
                                title="Download Unmatched Students"
                                onClick={() => this.handleDownloadCSV(assessment)}
                                style={{ marginLeft: '10px', cursor: 'pointer' }}
                              >
                                {assessment.parsed.studentsIgnored}
                              </Link>
                            ) : (
                              <Typography>
                                {assessment.parsed ? assessment.parsed.studentsIgnored || '-' : '-'}
                              </Typography> // Show "-" when no ignored students
                            )}
                          </TableCell>

                          <TableCell className={classes.tableCell} align="center">
                            <Typography>
                              {assessment &&
                                assessment.file &&
                                assessment.file.uploadedAt &&
                                moment
                                  .utc(assessment.file.uploadedAt.replace('d', '.')) //there is some wrong entries contains d instead of .
                                  .tz('America/Chicago')
                                  .format('M/D/YY h:mm A')}
                            </Typography>
                          </TableCell>

                          <TableCell className={classes.tableCell} align="center">
                            <Typography>
                              {assessment && assessment.date && moment(assessment.date).format('M/D/YY')}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tableCell} align="center">
                            <Typography>{assessment && assessment.cutYear}</Typography>
                          </TableCell>
                          {profile && (profile.type == 'superAdmin' || profile.isSuperAdmin == true) && (
                            <TableCell className={classes.tableCell} align="center">
                              <Typography>
                                <Checkbox
                                  checked={assessment.isVisibleBySuperAdminOnly == true}
                                  onChange={(e) =>
                                    this.handleAssessmentVisibilityBySuperAdminOnlyChange(assessment, e.target.checked)
                                  }
                                  color="primary"
                                />
                              </Typography>
                            </TableCell>
                          )}

                          {profile && profile.type == 'superAdmin' && (
                            <TableCell className={classes.tableCell} align="center">
                              <Typography>
                                <Button
                                  size="small"
                                  color="primary"
                                  className={classes.IconButton}
                                  onClick={(e) => this.handleDownload(assessment)}
                                >
                                  <Icon className={classes.center}>download</Icon>
                                </Button>
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                        {this.isExpandableEoc(assessment) && eocExpanded && (
                          <TableRow>
                            <TableCell
                              colSpan={colCount}
                              style={{
                                paddingTop: 0,
                                paddingBottom: 8,
                                paddingLeft: 56,
                                background: '#f5f5f5',
                                borderTop: 'none',
                              }}
                            >
                              {this.getAllEocFiles(assessment).map((file, idx) => {
                                const windowLabel = this.getAdminWindowLabel(file.administrationCode);
                                return (
                                  <div
                                    key={idx}
                                    style={{ display: 'flex', alignItems: 'center', padding: '4px 0', gap: 8 }}
                                  >
                                    <Icon style={{ fontSize: 14, color: '#888' }}>subdirectory_arrow_right</Icon>
                                    {windowLabel ? (
                                      <span
                                        style={{
                                          background: '#e3f2fd',
                                          color: '#1565c0',
                                          borderRadius: 12,
                                          padding: '2px 10px',
                                          fontSize: 11,
                                          fontWeight: 600,
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {windowLabel}
                                      </span>
                                    ) : null}
                                    <Typography variant="caption" style={{ color: '#555', fontFamily: 'monospace' }}>
                                      {file.origName || 'Unknown file'}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      style={{ color: '#aaa', marginLeft: 'auto', whiteSpace: 'nowrap' }}
                                    >
                                      {file.uploadedAt &&
                                        moment
                                          .utc(file.uploadedAt.replace('d', '.'))
                                          .tz('America/Chicago')
                                          .format('M/D/YY h:mm A')}
                                    </Typography>
                                  </div>
                                );
                              })}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </React.Fragment>
    );
  };

  getAssessmentTypesSelection = () => Object.keys(OtherAssessmentTypes);

  handleDownloadCSV = (assessment) => {
    if (
      assessment &&
      assessment.parsed &&
      assessment.parsed.studentsIgnored &&
      assessment.parsed.studentsIgnored > 0 &&
      assessment.parsed.ignoredStudentsList &&
      assessment.parsed.ignoredStudentsList.length > 0
    ) {
      this.DownloadIgnoredCSV(assessment);
    }
    return;
  };
  DownloadIgnoredCSV = (assessment) => {
    // Extract ignored records
    const ignoredStudentsList = assessment.parsed.ignoredStudentsList;

    // Define CSV Headers
    const headers = ['Local ID', 'Student ID', 'TSDS ID'];

    // Convert Data to CSV format
    const csvRows = [
      headers.join(','), // First row: Column names
      ...ignoredStudentsList.map((record) =>
        [`"${record.localId || ''}"`, `"${record.studentId || ''}"`, `"${record.tsdsId || ''}"`].join(',')
      ),
    ];
    // Convert CSV Rows to Blob
    const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });

    // Create a temporary URL for download
    const csvURL = window.URL.createObjectURL(csvData);
    const link = document.createElement('a');
    link.href = csvURL;
    link.download = `ignored_students_${assessment.name}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  shouldInit() {
    const { profile, needsInit, addAssessmentsListener, loading } = this.props;
    if (profile && needsInit && !loading) {
      addAssessmentsListener(profile);
    }
  }

  render() {
    const {
      classes,
      loading,
      profile,
      status,
      uploadStaging,
      onCancelUpload,
      uploadProgressMsg,
      uploadOpen,
      assessmentList,
    } = this.props;
    const { errorMsgs, assessmentToBeDeleted, isDeleteModalOpen, defaultAssessmentName, activeTab } = this.state;

    if (loading || !profile) {
      return (
        <div className={classes.root}>
          <div className={classes.content}>
            <LoadingCircle classes={classes} />
          </div>
        </div>
      );
    }

    // Todo: if not interim upload automatically
    // Todo: if interim or combo ask for name and date

    const { selectedYears } = this.state;

    return (
      <div className={classes.background}>
        <div className={classes.headerDiv}>
          <TitleBackButton title="Assessment Manager" />
        </div>
        <div className={classes.root}>
          {this.renderFilterSidebar()}
          <div className={classes.mainContent}>
            <UploadModal
              uploadStaging={uploadStaging}
              uploadProgressMsg={uploadProgressMsg}
              onCancelUpload={onCancelUpload}
              doUpload={this.doUpload}
              doUnrecognizedFileUpload={this.doUnrecognizedFileUpload}
              assessmentTypes={this.getAssessmentTypesSelection()}
              defaultAssessmentName={defaultAssessmentName}
            />
            <DeleteModal
              open={isDeleteModalOpen}
              assessment={assessmentToBeDeleted}
              cancelDelete={this.cancelDelete}
              doDelete={this.handleDelete}
            />

            {uploadOpen ? (
              this.renderUploadModal()
            ) : (
              <React.Fragment>
                <Tabs
                  value={activeTab}
                  onChange={this.handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  centered
                  style={{ marginBottom: 16 }}
                >
                  <Tab label="Individual Assessments" />
                  <Tab label="Assessment Groups" />
                </Tabs>
                {activeTab === 0 && this.renderAssessmentsList()}
                {activeTab === 1 &&
                  (assessmentList && assessmentList.length !== 0 ? (
                    <AssessmentGroupManager
                      assessmentList={assessmentList}
                      canDelete={this.canDelete}
                      renderOwner={this.renderOwner}
                      selectedYears={selectedYears}
                    />
                  ) : (
                    <Typography align="center">
                      {'Upload an assessment file first — groups are built from uploaded files.'}
                    </Typography>
                  ))}
              </React.Fragment>
            )}
            <div>
              {errorMsgs.map((msg, errIndex) => (
                <div key={`error-${errIndex}`}>
                  <Typography color="error">{msg}</Typography>
                </div>
              ))}
            </div>
            {status.error && (
              <Typography align="center" variant="subtitle1" color="error" className={classes.messages}>
                {status.error}
              </Typography>
            )}
            {status.message && (
              <Typography align="center" variant="subtitle1" color="primary" className={classes.messages}>
                {status.message}
              </Typography>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(AssessmentManager);
